/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { request } from '../../../config/helpers/axios-instance';

const POLL_INTERVAL_MS = 4000;

const STATUS_COLORS = {
    running: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
};

export default function DrugInteractionPanel({ orderId }) {
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const [job, setJob] = useState(null);
    const [error, setError] = useState('');
    const pollRef = useRef(null);

    const fetchLatestOrderJob = useCallback(async (refresh = false) => {
        if (!orderId) return;
        try {
            const response = await request({
                method: 'GET',
                url: `/userOrder/admin/drugInteraction/${orderId}?refresh=${refresh ? 'true' : 'false'}`,
            });
            if (response?.data?.success) {
                const payload = response.data.data || {};
                setJob({
                    status: payload?.status || 'idle',
                    jobId: payload?.jobId || null,
                    result: payload?.result || null,
                    error: payload?.error || null,
                });
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Unable to fetch interaction status');
        }
    }, [orderId]);

    useEffect(() => {
        if (!orderId) return;
        setLoading(true);
        fetchLatestOrderJob().finally(() => setLoading(false));
    }, [orderId, fetchLatestOrderJob]);

    const isRunning = job?.status === 'running';

    useEffect(() => {
        if (!isRunning) {
            if (pollRef.current) clearInterval(pollRef.current);
            return;
        }
        pollRef.current = setInterval(fetchLatestOrderJob, POLL_INTERVAL_MS);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [isRunning, fetchLatestOrderJob]);

    const startInteractionCheck = async () => {
        if (!orderId) return;
        setStarting(true);
        setError('');
        try {
            await fetchLatestOrderJob(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to start interaction check');
        } finally {
            setStarting(false);
        }
    };

    const report = job?.result?.report || null;
    const hasResult = Boolean(report);
    const medicineInteractions = report?.medicine_interactions || [];
    const saltInteractions = report?.interactions || [];

    const statusText = useMemo(() => {
        if (!job) return 'Not started';
        return job.status || 'unknown';
    }, [job]);

    return (
        <div className='bg-white border border-gray-200 rounded-2xl p-4 shadow-soft-xl flex flex-col gap-3'>
            <div className='flex items-center justify-between'>
                <div>
                    <h3 className='text-base font-semibold text-gray-800'>Drug Interactions (WebMD)</h3>
                    <p className='text-xs text-gray-500'>Background job linked to this order</p>
                </div>
                <div className='flex items-center gap-2'>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[statusText] || 'bg-gray-100 text-gray-700'}`}>
                        {statusText}
                    </span>
                    <button
                        type='button'
                        className='px-3 py-1.5 rounded-lg bg-blue-500 text-white-50 text-sm font-medium hover:bg-blue-600 disabled:opacity-60'
                        disabled={starting || isRunning || loading}
                        onClick={startInteractionCheck}
                    >
                        {starting ? 'Starting...' : isRunning ? 'Running...' : 'Run Check'}
                    </button>
                </div>
            </div>

            {loading && <p className='text-sm text-gray-500'>Loading interaction status...</p>}
            {error && <p className='text-sm text-red-600'>{error}</p>}

            {job?.jobId && (
                <p className='text-xs text-gray-500'>
                    Job: <span className='font-mono'>{job.jobId}</span>
                </p>
            )}

            {hasResult && (
                <div className='flex flex-col gap-2 text-sm'>
                    <p className='text-gray-700'>
                        <span className='font-medium'>Medicine interactions found:</span> {report.medicine_interaction_count || 0}
                    </p>
                    {!!report?.salts_checked?.length && (
                        <p className='text-gray-700'>
                            <span className='font-medium'>Salts checked:</span> {report.salts_checked.join(', ')}
                        </p>
                    )}
                    {!!report?.salts_not_found_on_webmd?.length && (
                        <p className='text-amber-700'>
                            <span className='font-medium'>Not found on WebMD:</span> {report.salts_not_found_on_webmd.join(', ')}
                        </p>
                    )}
                    {!!medicineInteractions.length && (
                        <div className='border border-gray-200 rounded-lg p-2 bg-gray-50 max-h-48 overflow-auto'>
                            {medicineInteractions.map((item, idx) => (
                                <div key={`${item.medicine_pair?.join('-') || 'medicine-pair'}-${idx}`} className='mb-3 last:mb-0'>
                                    <p className='font-medium text-gray-800'>
                                        {(item.medicine_pair || []).join(' <> ')}
                                    </p>
                                    <p className='text-xs text-gray-500'>
                                        Severities: {(item.severities || []).join(', ') || 'Unknown'}
                                    </p>
                                    {!!item?.salt_interactions?.length && (
                                        <div className='mt-1 pl-3 border-l border-gray-300 flex flex-col gap-1'>
                                            {item.salt_interactions.map((saltItem, saltIdx) => (
                                                <p key={`${saltIdx}-${saltItem?.salt_pair?.join('-')}`} className='text-xs text-gray-600'>
                                                    {(saltItem?.salt_pair || []).join(' + ')} ({saltItem?.severity || 'Unknown'})
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {!!saltInteractions.length && (
                        <details className='border border-gray-200 rounded-lg p-2 bg-white'>
                            <summary className='cursor-pointer text-xs font-medium text-gray-700'>
                                View salt-wise interactions ({report.interaction_count || 0})
                            </summary>
                            <div className='mt-2 max-h-40 overflow-auto'>
                                {saltInteractions.map((item, idx) => (
                                    <div key={`${item.drug_pair?.join('-') || 'salt-pair'}-${idx}`} className='mb-2 last:mb-0'>
                                        <p className='text-xs font-medium text-gray-800'>
                                            {(item.drug_pair || []).join(' + ')} ({item.severity})
                                        </p>
                                        <p className='text-xs text-gray-600'>{item.description || 'No description'}</p>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
}
